import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n";
import { formatAmount, formatMoney } from "../utils/money";

const Badge = ({ tone = "primary", pulse = false, children }) => (
  <span
    className={`absolute -top-1.5 -end-1.5 min-w-5 h-5 px-1 rounded-full text-[11px] font-bold flex items-center justify-center ${
      tone === "warning"
        ? "bg-amber-500 text-slate-900"
        : "bg-primary-500 text-white"
    } ${pulse ? "ring-2 ring-amber-300/60 animate-pulse" : ""}`}
  >
    {children}
  </span>
);

// `wide`: on a phone the buttons of the second row share its width equally
const IconButton = ({ label, badge, badgeTone, pulse, onClick, wide = false, order = "", children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className={`relative h-10 sm:h-11 sm:w-11 rounded-xl bg-slate-800/80 border border-slate-700 text-lg text-slate-200 hover:bg-slate-700 hover:text-white active:scale-95 transition-all flex items-center justify-center ${
      wide ? "flex-1 min-w-0 sm:flex-none" : "shrink-0 w-10"
    } ${order}`}
  >
    {children}
    {badge > 0 && (
      <Badge tone={badgeTone} pulse={pulse}>
        {badge}
      </Badge>
    )}
  </button>
);

/**
 * Register top bar: who and where, the day's takings, and the doors out.
 *
 * On a phone it is two rows, because one cannot hold them all:
 *  - **row 1** — shop and cashier, the takings (also the way into today's
 *    sales) and 🔒: always on screen, since handing the till over must never
 *    need a scroll;
 *  - **row 2** — 📦 pickups, 💵 drawer, 💧 doses, 🚚 supplier, ⏸ held tickets, ⚙️ manage.
 *    It slides away while the list is scrolled down, to give the products the
 *    room, and comes back on any scroll up (`actionsHidden`, decided by the
 *    register) — never only at the top of a long list, so nothing is more than
 *    a small scroll away. While it is hidden an amber chip on row 1 counts
 *    what is waiting (pickups due, held tickets) and brings it back when
 *    tapped.
 * From `sm` up there is room for one row and none of this applies.
 *
 * The header is `fixed`, with a spacer of the same height in the flow: hiding
 * row 2 changes only the header's own box, never the page's scrollable height
 * (a sticky, in-flow header that resized is what made the filter collapse
 * flicker before — see PosRegister). `PosRegister` moves the search row by the
 * same distance, so its height must stay in step with `ACTIONS_ROW_REM`.
 *
 * Signing in is not a button at all, because the till does not open without a
 * PIN; held tickets only appear when there is one to resume; the customer
 * card lives on the ticket it belongs to.
 *
 * Pickups due today (or already late) are badged in amber on 📦, so an order
 * someone is on their way to collect is never a surprise at the counter.
 */
const PosHeader = ({
  storeName,
  cashierName,
  currency,
  todayRevenue,
  todaySales,
  heldCount,
  unsyncedCount,
  pickupsDue = 0,
  actionsHidden = false,
  onRevealActions,
  onOpenHeld,
  onOpenHistory,
  onOpenPickups,
  onOpenSupplier,
  onOpenDoses,
  onOpenDrawer,
  drawerAmount,
  onLock,
}) => {
  const { t } = useI18n();
  const sales = t("common.sales", { count: todaySales });
  // What the hidden row would be showing: reason enough to bring it back
  const waiting = pickupsDue + heldCount;

  return (
    <>
      {/* The header is fixed; this keeps its place in the page */}
      <div aria-hidden="true" className="h-[5.75rem] sm:h-14" />

      <header className="fixed inset-x-0 top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:h-14 sm:gap-2 sm:px-4">
          {/* Row 1 — on sm and up the two rows are one, ordered by `sm:order-*` */}
          <div className="flex items-center gap-1.5 h-12 px-3 sm:contents">
            <div className="min-w-0 flex-1 flex items-center gap-2 sm:order-1">
              <span className="shrink-0 text-xl leading-none" aria-hidden="true">
                💨
              </span>
              <div className="min-w-0">
                <p className="text-sm sm:text-base font-bold text-white truncate leading-tight">
                  {storeName}
                </p>
                <p className="text-[10px] uppercase tracking-widest text-primary-400 font-semibold leading-tight truncate">
                  {t("pos.header.register")}
                  {cashierName ? ` · ${cashierName}` : ""}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onOpenHistory}
              aria-label={t("pos.header.todayAria", {
                sales,
                amount: formatMoney(todayRevenue, currency),
              })}
              className="relative shrink-0 flex flex-col items-end px-2.5 sm:px-3 py-1 rounded-xl bg-slate-800/60 border border-slate-700 hover:bg-slate-700/60 transition-colors sm:order-2"
            >
              <span className="text-sm font-bold text-emerald-400 leading-tight tabular-nums">
                {formatAmount(todayRevenue)}
                <span className="hidden sm:inline"> {currency}</span>
              </span>
              <span className="text-[10px] text-slate-400 leading-tight">
                {sales}
                <span className="hidden sm:inline"> {t("pos.header.today")}</span>
              </span>
              {/* Sales still waiting to reach the server, so they are never out of sight */}
              {unsyncedCount > 0 && <Badge tone="warning">{unsyncedCount}</Badge>}
            </button>

            {actionsHidden && waiting > 0 && (
              <button
                type="button"
                onClick={onRevealActions}
                aria-label={t("pos.header.showActions", { count: waiting })}
                className="sm:hidden shrink-0 h-8 min-w-8 px-2 rounded-full bg-amber-500 text-slate-900 text-xs font-bold tabular-nums animate-pulse"
              >
                {waiting}
              </button>
            )}

            <IconButton
              label={
                cashierName
                  ? t("pos.header.lockAs", { name: cashierName })
                  : t("pos.header.lock")
              }
              onClick={onLock}
              order="sm:order-7"
            >
              🔒
            </IconButton>
          </div>

          {/* Row 2 — slides away under row 1 while scrolling down (phones only) */}
          <div
            inert={actionsHidden}
            className={`flex items-center gap-1.5 px-3 h-11 overflow-hidden transition-[max-height,opacity] duration-200 ease-out sm:contents ${
              actionsHidden ? "max-h-0 opacity-0" : "max-h-11 opacity-100"
            }`}
          >
            {onOpenPickups && (
            <IconButton
              label={
                pickupsDue > 0
                  ? t("pos.header.pickupsDue", { count: pickupsDue })
                  : t("pos.header.pickups")
              }
              badge={pickupsDue}
              badgeTone="warning"
              pulse
              onClick={onOpenPickups}
              wide
              order="sm:order-3"
            >
              📦
            </IconButton>
            )}

            {/* The drawer; what it should hold only for someone who manages cash */}
            {onOpenDrawer && (
              <button
                type="button"
                onClick={onOpenDrawer}
                title={t("pos.header.drawer")}
                aria-label={t("pos.header.drawer")}
                className="flex-1 min-w-0 sm:flex-none h-10 sm:h-11 sm:min-w-11 px-2 rounded-xl bg-slate-800/80 border border-slate-700 text-lg text-slate-200 hover:bg-slate-700 active:scale-95 transition-all flex items-center justify-center gap-1 sm:order-4"
              >
                💵
                {drawerAmount !== undefined && (
                  <span className="text-xs font-bold tabular-nums text-emerald-300" dir="ltr">
                    {formatAmount(drawerAmount)}
                  </span>
                )}
              </button>
            )}

            {/* Selling by the dose, once a product is sold that way */}
            {onOpenDoses && (
              <IconButton
                label={t("doses.button")}
                onClick={onOpenDoses}
                wide
                order="sm:order-5"
              >
                💧
              </IconButton>
            )}

            {/* Only for a cashier who may receive stock or pay from the drawer */}
            {onOpenSupplier && (
              <IconButton
                label={t("pos.header.supplier")}
                onClick={onOpenSupplier}
                wide
                order="sm:order-5"
              >
                🚚
              </IconButton>
            )}

            {heldCount > 0 && (
              <IconButton
                label={t("pos.header.held")}
                badge={heldCount}
                onClick={onOpenHeld}
                wide
                order="sm:order-6"
              >
                ⏸
              </IconButton>
            )}

            <Link
              to="/admin"
              title={t("pos.header.manage")}
              aria-label={t("pos.header.manage")}
              className="flex-1 min-w-0 sm:flex-none h-10 sm:h-11 sm:min-w-11 px-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:text-white active:scale-95 transition-all flex items-center justify-center gap-2 sm:order-8"
            >
              <span className="text-lg">⚙️</span>
              <span className="hidden lg:inline text-sm font-semibold">
                {t("pos.header.manageShort")}
              </span>
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

export default PosHeader;
